import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import client from '../api/client';

const LEVEL_STYLE: Record<string, any> = {
  저관여: { color: '#00D4AA', bg: '#0F2A22', label: '🟢 저관여' },
  중간관여: { color: '#F6AD55', bg: '#2A1F0D', label: '🟡 중간관여' },
  고관여: { color: '#F56565', bg: '#2A0D0D', label: '🔴 고관여' },
};

const TYPE_COLOR: Record<string, string> = {
  구매의도: '#00D4AA', 콘텐츠: '#378ADD', 노출: '#F6AD55', 일반: '#8892A4'
};

const ScoreBadge = ({ score }: { score: number }) => {
  const color = score >= 8 ? '#00D4AA' : score >= 5 ? '#F6AD55' : '#F56565';
  return (
    <div style={{ background: color + '22', border: `1px solid ${color}`, borderRadius: 6, padding: '2px 10px', color, fontSize: 13, fontWeight: 700, minWidth: 36, textAlign: 'center' }}>
      {score}
    </div>
  );
};

export default function KeywordStrategy() {
  const checkAuth = useAuthGuard();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [mainKeyword, setMainKeyword] = useState('');
  const [subKeywords, setSubKeywords] = useState(['', '', '']);
  const [includeKeywords, setIncludeKeywords] = useState(['', '', '']);
  const [includeMode, setIncludeMode] = useState<'OR' | 'AND'>('OR');
  const [excludeKeywords, setExcludeKeywords] = useState(['', '', '']);
  const [excludeMode, setExcludeMode] = useState<'OR' | 'AND'>('OR');
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<'create' | 'list'>('create');
  const [savedList, setSavedList] = useState<any[]>([]);

  const loadData = async () => {
    try {
      const [st, kw] = await Promise.all([
        client.get('/marketing/list'),
        client.get('/keyword-strategy/list'),
      ]);
      setStrategies(st.data.strategies);
      setSavedList(kw.data.strategies);
    } catch {}
  };

  useEffect(() => { loadData(); }, []);

  const getFiltered = (keywords: any[]) => {
    if (!filterEnabled) return keywords;
    const incl = includeKeywords.filter(k => k.trim());
    const excl = excludeKeywords.filter(k => k.trim());
    return keywords.filter((kw: any) => {
      const name = kw.keyword.toLowerCase();
      if (excl.length > 0) {
        const isExcluded = excludeMode === 'AND'
          ? excl.every(e => name.includes(e.toLowerCase()))
          : excl.some(e => name.includes(e.toLowerCase()));
        if (isExcluded) return false;
      }
      if (incl.length === 0) return true;
      if (includeMode === 'AND') return incl.every(i => name.includes(i.toLowerCase()));
      return incl.some(i => name.includes(i.toLowerCase()));
    });
  };

  const handleAnalyze = async () => {
    if (!checkAuth()) return;
    if (!mainKeyword.trim()) return alert('메인 키워드를 입력해주세요');
    setLoading(true); setSaved(false);
    try {
      const res = await client.post('/keyword-strategy/analyze', {
        marketing_id: selected.id,
        main_keyword: mainKeyword.trim(),
        sub_keywords: subKeywords.filter(k => k.trim()),
      });
      const filteredKeywords = getFiltered(res.data.keywords || []);
      const resultWithFilter = { ...res.data, keywords: filteredKeywords, total_before_filter: res.data.keywords?.length };
      setResult(resultWithFilter);
      await client.post('/keyword-strategy/save', {
        marketing_id: selected.id,
        main_keyword: mainKeyword.trim(),
        result_json: JSON.stringify(filteredKeywords),
      });
      setSaved(true);
      await loadData();
    } catch { alert('분석 실패'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제할까요?')) return;
    try {
      await client.delete(`/keyword-strategy/${id}`);
      await loadData();
    } catch { alert('삭제 실패'); }
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 14, width: '100%', boxSizing: 'border-box' as const },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
    btnBlue: { background: '#1A3A5C', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#378ADD', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 20px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
    label: { color: '#B0BAC8', fontSize: 12, marginBottom: 6, display: 'block' },
  };

  const ModeBtn = ({ mode, current, color, onClick }: any) => (
    <button onClick={onClick} style={{
      background: current === mode ? '#1A2035' : 'none',
      border: `1px solid ${current === mode ? color : '#2A3045'}`,
      borderRadius: 6, padding: '3px 12px',
      color: current === mode ? color : '#4A5568',
      fontSize: 12, cursor: 'pointer', fontWeight: current === mode ? 700 : 400
    }}>{mode}</button>
  );

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>Step 3. 키워드 전략</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>관여도에 맞는 키워드 적합도를 분석합니다</div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button style={s.tab(tab === 'create')} onClick={() => { setTab('create'); setSelected(null); setResult(null); }}>분석하기</button>
          <button style={s.tab(tab === 'list')} onClick={() => setTab('list')}>저장된 분석 ({savedList.length})</button>
        </div>

        {tab === 'create' && (
          <>
            {strategies.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
                <div style={{ color: '#F6AD55', fontSize: 14, marginBottom: 8 }}>⚠️ 먼저 Step 2. 마케팅 전략을 완료해주세요</div>
                <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>키워드 전략은 마케팅 전략 결과를 기반으로 분석됩니다</div>
                <button style={s.btn} onClick={() => navigate('/marketing')}>마케팅 전략 하러가기</button>
              </div>
            ) : (
              <>
                {!selected && (
                  <>
                    <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 12 }}>마케팅 전략을 선택하세요</div>
                    {strategies.map(st => {
                      const lv = LEVEL_STYLE[st.level];
                      return (
                        <div key={st.id} style={{ ...s.card, cursor: 'pointer' }}
                          onClick={() => { setSelected(st); setMainKeyword(''); setSubKeywords(['','','']); setIncludeKeywords(['','','']); setExcludeKeywords(['','','']); setFilterEnabled(false); setResult(null); }}
                          onMouseEnter={e => e.currentTarget.style.border = `0.5px solid ${lv.color}`}
                          onMouseLeave={e => e.currentTarget.style.border = '0.5px solid #2A3045'}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{st.title}</div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span style={{ background: lv.color + '22', border: `1px solid ${lv.color}55`, borderRadius: 6, padding: '2px 10px', color: lv.color, fontSize: 12, fontWeight: 600 }}>{lv.label}</span>
                                <span style={{ color: '#8892A4', fontSize: 12 }}>{st.channels}</span>
                              </div>
                            </div>
                            <span style={{ color: '#00D4AA', fontSize: 13 }}>선택 →</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {selected && (
                  <>
                    <div style={{ ...s.card, background: LEVEL_STYLE[selected.level]?.bg, border: `1px solid ${LEVEL_STYLE[selected.level]?.color}33`, marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{selected.title}</div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <span style={{ background: LEVEL_STYLE[selected.level]?.color + '22', border: `1px solid ${LEVEL_STYLE[selected.level]?.color}`, borderRadius: 6, padding: '2px 10px', color: LEVEL_STYLE[selected.level]?.color, fontSize: 12, fontWeight: 600 }}>{LEVEL_STYLE[selected.level]?.label}</span>
                          </div>
                        </div>
                        <button style={s.btnGhost} onClick={() => setSelected(null)}>다시 선택</button>
                      </div>
                    </div>

                    <div style={s.card}>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>키워드 입력</div>

                      <label style={s.label}>메인 키워드 (필수)</label>
                      <input style={{ ...s.input, marginBottom: 14 }} value={mainKeyword}
                        onChange={e => setMainKeyword(e.target.value)}
                        placeholder="예) 전주 떡케이크" />

                      <label style={s.label}>서브 키워드 (선택, 최대 3개)</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                        {subKeywords.map((kw, i) => (
                          <input key={i} style={{ ...s.input, marginBottom: 0 }} value={kw}
                            onChange={e => { const n = [...subKeywords]; n[i] = e.target.value; setSubKeywords(n); }}
                            placeholder={`서브 키워드 ${i + 1}`} />
                        ))}
                      </div>

                      {/* 필터 토글 */}
                      <div style={{ borderTop: '0.5px solid #1E2535', paddingTop: 16 }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 10, background: filterEnabled ? '#0F2A22' : '#0F1117', border: `1px solid ${filterEnabled ? '#00D4AA44' : '#2A3045'}`, borderRadius: 8, padding: '10px 14px', cursor: result ? 'default' : 'pointer', marginBottom: filterEnabled ? 16 : 0, opacity: result ? 0.6 : 1 }}
                          onClick={() => { if (!result) setFilterEnabled(f => !f); }}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${filterEnabled ? '#00D4AA' : '#4A5568'}`, background: filterEnabled ? '#00D4AA' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {filterEnabled && <span style={{ color: '#0A1F18', fontSize: 12, fontWeight: 900 }}>✓</span>}
                          </div>
                          <div>
                            <div style={{ color: filterEnabled ? '#00D4AA' : '#8892A4', fontSize: 13, fontWeight: 600 }}>결과 필터 적용 (선택사항)</div>
                            <div style={{ color: '#4A5568', fontSize: 11, marginTop: 2 }}>{result ? '⚠️ 분석 전에 설정해야 합니다. 재분석하려면 다시 설정하세요.' : '포함/제외 키워드로 결과를 필터링합니다. 분석 시작 전에 설정하세요.'}</div>
                          </div>
                        </div>

                        {filterEnabled && (
                          <div>
                            {/* 필수 포함 */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <div>
                                  <span style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600 }}>✅ 필수 포함 키워드</span>
                                  <span style={{ color: '#4A5568', fontSize: 11, marginLeft: 8 }}>최대 3개</span>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <ModeBtn mode="OR" current={includeMode} color="#00D4AA" onClick={() => setIncludeMode('OR')} />
                                  <ModeBtn mode="AND" current={includeMode} color="#00D4AA" onClick={() => setIncludeMode('AND')} />
                                </div>
                              </div>
                              <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 8 }}>
                                {includeMode === 'OR'
                                  ? '💡 OR — 입력한 키워드 중 하나라도 포함된 결과만 표시 (예: "바람떡" OR "떡케이크")'
                                  : '💡 AND — 입력한 키워드를 모두 포함하는 결과만 표시 (예: "전주" AND "떡케이크")'}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                {includeKeywords.map((kw, i) => (
                                  <input key={i} style={{ ...s.input, marginBottom: 0 }} value={kw}
                                    onChange={e => { const n = [...includeKeywords]; n[i] = e.target.value; setIncludeKeywords(n); }}
                                    placeholder={`포함 ${i + 1} (예: 전주)`} />
                                ))}
                              </div>
                            </div>

                            {/* 제외 키워드 */}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                <div>
                                  <span style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600 }}>❌ 제외 키워드</span>
                                  <span style={{ color: '#4A5568', fontSize: 11, marginLeft: 8 }}>최대 3개</span>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <ModeBtn mode="OR" current={excludeMode} color="#F56565" onClick={() => setExcludeMode('OR')} />
                                  <ModeBtn mode="AND" current={excludeMode} color="#F56565" onClick={() => setExcludeMode('AND')} />
                                </div>
                              </div>
                              <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 8 }}>
                                {excludeMode === 'OR'
                                  ? '💡 OR — 입력한 키워드 중 하나라도 있으면 결과에서 제외 (예: "군산" OR "익산")'
                                  : '💡 AND — 입력한 키워드를 모두 포함할 때만 제외 (예: "군산" AND "떡집")'}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                {excludeKeywords.map((kw, i) => (
                                  <input key={i} style={{ ...s.input, marginBottom: 0 }} value={kw}
                                    onChange={e => { const n = [...excludeKeywords]; n[i] = e.target.value; setExcludeKeywords(n); }}
                                    placeholder={`제외 ${i + 1} (예: 군산)`} />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                      <button style={s.btn} onClick={handleAnalyze} disabled={loading}>
                        {loading ? '분석 중...' : '키워드 분석 시작'}
                      </button>
                    </div>

                    {result && (
                      <div style={s.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                          <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>
                            키워드 분석 결과 ({result.keywords?.length}개{result.total_before_filter && result.total_before_filter !== result.keywords?.length ? ` / 필터 전 ${result.total_before_filter}개` : ''})
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {saved && <span style={{ color: '#00D4AA', fontSize: 13 }}>✓ 저장됨</span>}
                            <button style={s.btnBlue} onClick={() => navigate('/report')}>Step 4. 전략 리포트 →</button>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
                          {Object.entries(TYPE_COLOR).map(([type, color]) => (
                            <span key={type} style={{ background: color + '22', border: `1px solid ${color}55`, borderRadius: 4, padding: '2px 8px', color, fontSize: 11 }}>{type}</span>
                          ))}
                          <span style={{ color: '#4A5568', fontSize: 11, marginLeft: 8 }}>적합도: 8~10 🟢 5~7 🟡 1~4 🔴</span>
                        </div>

                        <div style={{ overflowX: 'auto' as const }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#1A2035' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left' as const, color: '#8892A4', borderBottom: '0.5px solid #2A3045' }}>키워드</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center' as const, color: '#8892A4', borderBottom: '0.5px solid #2A3045' }}>월 검색량</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center' as const, color: '#8892A4', borderBottom: '0.5px solid #2A3045' }}>유형</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center' as const, color: '#8892A4', borderBottom: '0.5px solid #2A3045' }}>적합도</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(result.keywords || []).map((kw: any, i: number) => (
                                <tr key={i} style={{ background: kw.is_main ? '#1A2035' : i % 2 === 0 ? '#161B27' : '#0F1117' }}>
                                  <td style={{ padding: '8px 12px', color: '#E8ECF4', borderBottom: '0.5px solid #1E2535' }}>
                                    {kw.is_main && <span style={{ color: '#00D4AA', fontSize: 10, marginRight: 6 }}>메인</span>}
                                    {kw.keyword}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' as const, color: '#B0BAC8', borderBottom: '0.5px solid #1E2535' }}>
                                    {kw.monthly_search?.toLocaleString()}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' as const, borderBottom: '0.5px solid #1E2535' }}>
                                    <span style={{ background: TYPE_COLOR[kw.type] + '22', border: `1px solid ${TYPE_COLOR[kw.type]}55`, borderRadius: 4, padding: '2px 8px', color: TYPE_COLOR[kw.type], fontSize: 11 }}>{kw.type}</span>
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' as const, borderBottom: '0.5px solid #1E2535' }}>
                                    <ScoreBadge score={kw.score} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {tab === 'list' && (
          <div>
            {savedList.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center' as const, color: '#4A5568', padding: 40 }}>저장된 분석이 없습니다</div>
            ) : savedList.map(r => {
              const lv = LEVEL_STYLE[r.level];
              return (
                <div key={r.id} style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{r.title}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ background: lv?.color + '22', border: `1px solid ${lv?.color}55`, borderRadius: 6, padding: '2px 10px', color: lv?.color, fontSize: 12, fontWeight: 600 }}>{lv?.label}</span>
                        <span style={{ color: '#8892A4', fontSize: 12 }}>메인: {r.main_keyword}</span>
                        <span style={{ color: '#4A5568', fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString('ko-KR')}</span>
                      </div>
                    </div>
                    <button style={s.btnGhost} onClick={() => handleDelete(r.id)}>삭제</button>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#1A2035' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left' as const, color: '#8892A4' }}>키워드</th>
                        <th style={{ padding: '6px 10px', textAlign: 'center' as const, color: '#8892A4' }}>월검색량</th>
                        <th style={{ padding: '6px 10px', textAlign: 'center' as const, color: '#8892A4' }}>유형</th>
                        <th style={{ padding: '6px 10px', textAlign: 'center' as const, color: '#8892A4' }}>적합도</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.keywords?.slice(0, 10).map((kw: any, i: number) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#161B27' : '#0F1117' }}>
                          <td style={{ padding: '6px 10px', color: '#E8ECF4' }}>{kw.keyword}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' as const, color: '#B0BAC8' }}>{kw.monthly_search?.toLocaleString()}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' as const }}>
                            <span style={{ color: TYPE_COLOR[kw.type], fontSize: 11 }}>{kw.type}</span>
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' as const }}>
                            <ScoreBadge score={kw.score} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
